import { useState, useEffect } from 'react';
import { useLogicModel, Output } from '../../../contexts/LogicModelContext';
import { supabase } from '../../../lib/supabase';
import { useTenant } from '../../../hooks/useTenant';

const METRIC_TYPES = [
  { value: 'count', label: '# Count', icon: '📊', example: '150 participants' },
  { value: 'percentage', label: '% Percentage', icon: '📈', example: '85% completion rate' },
  { value: 'currency', label: '$ Currency', icon: '💵', example: '$50,000 distributed' },
  { value: 'hours', label: '⏱️ Hours', icon: '⏱️', example: '500 training hours' },
];

export function OutputsStep() {
  const { logicModel, updateOutputs, markStepComplete } = useLogicModel();
  const { organization } = useTenant();
  const [outputs, setOutputs] = useState<Output[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    metric_type: 'count' as 'count' | 'percentage' | 'currency' | 'hours',
    target_value: '',
    actual_value: '',
    unit: '',
  });

  useEffect(() => {
    if (logicModel?.outputs) {
      setOutputs(logicModel.outputs);
    }
  }, [logicModel?.outputs]);

  useEffect(() => {
    if (outputs.length > 0) {
      markStepComplete('outputs');
    }
  }, [outputs.length, markStepComplete]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      metric_type: 'count',
      target_value: '',
      actual_value: '',
      unit: '',
    });
    setEditingId(null);
    setIsAdding(false);
  };

  const handleEdit = (output: Output) => {
    setFormData({
      name: output.name,
      description: output.description || '',
      metric_type: output.metric_type || 'count',
      target_value: output.target_value?.toString() || '',
      actual_value: output.actual_value?.toString() || '',
      unit: output.unit || '',
    });
    setEditingId(output.id || null);
    setIsAdding(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Output name is required');
      return;
    }

    if (!logicModel?.id || !organization) return;

    setSaving(true);
    setError(null);

    try {
      const outputData = {
        name: formData.name,
        description: formData.description || null,
        metric_type: formData.metric_type,
        target_value: formData.target_value ? parseFloat(formData.target_value) : null,
        actual_value: formData.actual_value ? parseFloat(formData.actual_value) : null,
        unit: formData.unit || null,
      };

      if (editingId) {
        const { error: updateError } = await supabase
          .from('logic_model_outputs')
          .update(outputData)
          .eq('id', editingId);

        if (updateError) throw updateError;

        const updated = outputs.map(o =>
          o.id === editingId ? { ...o, ...outputData } : o
        );
        setOutputs(updated);
        updateOutputs(updated);
      } else {
        const { data: newOutput, error: insertError } = await supabase
          .from('logic_model_outputs')
          .insert({
            logic_model_id: logicModel.id,
            organization_id: organization.organization_id,
            ...outputData,
            sort_order: outputs.length,
          })
          .select()
          .single();

        if (insertError) throw insertError;

        const newOutputs = [...outputs, newOutput];
        setOutputs(newOutputs);
        updateOutputs(newOutputs);
      }

      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (outputId: string) => {
    if (!confirm('Delete this output?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('logic_model_outputs')
        .delete()
        .eq('id', outputId);

      if (deleteError) throw deleteError;

      const updated = outputs.filter(o => o.id !== outputId);
      setOutputs(updated);
      updateOutputs(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const getProgressPercent = (target?: number | null, actual?: number | null) => {
    if (!target || target === 0) return 0;
    return Math.min(100, Math.max(0, ((actual || 0) / target) * 100));
  };

  const formatValue = (value: number | null | undefined, type: string | null | undefined) => {
    if (value === null || value === undefined) return '—';
    switch (type) {
      case 'currency':
        return `$${value.toLocaleString()}`;
      case 'percentage':
        return `${value}%`;
      default:
        return value.toLocaleString();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          📊 Define Outputs & Metrics
        </h2>
        <p className="text-gray-600">
          Outputs are the direct, countable products of your activities. They confirm implementation happened as planned.
        </p>
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h3 className="font-medium text-purple-900 mb-2">⚠️ Outputs vs Outcomes</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-purple-800">❌ Output (what you did)</span>
            <p className="text-purple-700">"We trained 25 people"</p>
          </div>
          <div>
            <span className="font-medium text-purple-800">✅ Outcome (what changed)</span>
            <p className="text-purple-700">"18 gained job-ready skills, 12 got employed"</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Outputs List */}
      {outputs.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-medium text-gray-900">Your Outputs ({outputs.length})</h3>
          {outputs.map((output) => (
            <div
              key={output.id}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">
                      {METRIC_TYPES.find(m => m.value === output.metric_type)?.icon || '📊'}
                    </span>
                    <h4 className="font-medium text-gray-900">{output.name}</h4>
                  </div>
                  {output.description && (
                    <p className="text-sm text-gray-600 mt-1 ml-7">{output.description}</p>
                  )}
                  
                  {/* Progress visualization */}
                  <div className="mt-3 ml-7">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Actual: {formatValue(output.actual_value, output.metric_type)}{output.unit ? ` ${output.unit}` : ''}</span>
                      <span>Target: {formatValue(output.target_value, output.metric_type)}{output.unit ? ` ${output.unit}` : ''}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full transition-all"
                        style={{ width: `${getProgressPercent(output.target_value, output.actual_value)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-right">
                      {getProgressPercent(output.target_value, output.actual_value).toFixed(0)}% of target
                    </p>
                  </div>
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => handleEdit(output)}
                    className="text-gray-400 hover:text-blue-600"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => output.id && handleDelete(output.id)}
                    className="text-gray-400 hover:text-red-600"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form */}
      {isAdding ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <h3 className="font-medium text-gray-900">
            {editingId ? 'Edit Output' : 'Add Output'}
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Output Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
              placeholder="e.g., Training sessions conducted"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
              placeholder="What does this output measure?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Metric Type
              </label>
              <select
                value={formData.metric_type}
                onChange={(e) => setFormData({ ...formData, metric_type: e.target.value as any })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
              >
                {METRIC_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit (optional)
              </label>
              <input
                type="text"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                placeholder="e.g., people, sessions"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Value
              </label>
              <input
                type="number"
                value={formData.target_value}
                onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                placeholder="100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Actual Value
              </label>
              <input
                type="number"
                value={formData.actual_value}
                onChange={(e) => setFormData({ ...formData, actual_value: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                placeholder="—"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={resetForm}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md disabled:opacity-50"
            >
              {saving ? 'Saving...' : editingId ? 'Update' : 'Add Output'}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsAdding(true)}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-400 hover:text-purple-600 transition-colors"
        >
          + Add Output
        </button>
      )}

      {/* Metric Types Reference */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-3">📏 Metric Types</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {METRIC_TYPES.map((type) => (
            <div key={type.value} className="text-sm">
              <span className="font-medium">{type.icon} {type.label}</span>
              <p className="text-gray-500 text-xs">{type.example}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
